import { defineStore } from 'pinia';
import {
  ChangePassword,
  RegisterUser,
  UserDetail,
  PaginatedUsers,
  Role,
  SimpleUser,
  ChangeRole,
  ValidateInviteToken,
  ValidateInviteTokenResponse,
  PaginatedInvitations,
  DeleteInvitedUser,
  AssignFarm
} from '@/store/models/user';
import { QTablePagination } from '@/store/models/meta';
import { emptyPage } from '@/store/models/page';
import UserAPI from '@/store/api/user';
import {
  InviteAdmin,
  InviteConsultant,
  InviteManager,
  InviteMarketer,
  InviteOwner
} from '@/store/models/jwt';
import { buildQuery } from '@/utils/query';

export const useUserStore = defineStore('user', {
  state: () => ({
    Roles: [] as Role[],
    user: {} as RegisterUser,
    UserDetail: {} as UserDetail,
    Users: emptyPage<PaginatedUsers>(),
    SimpleUserList: [] as SimpleUser[],
    UserTablePagination: {
      sortBy: 'id',
      descending: false,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 1
    } as QTablePagination,
    PaginatedInvitation: emptyPage<PaginatedInvitations>(),
    InvitationTablePagination: {
      sortBy: 'created_date',
      descending: true,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 1
    } as QTablePagination
  }),
  actions: {
    setUserTablePagination(data: QTablePagination) {
      this.UserTablePagination = data;
    },
    async getUserRoles() {
      this.Roles = await UserAPI.getUserRoles();
    },
    async inviteAdmin(inviteEmail: InviteAdmin[]) {
      return await UserAPI.inviteAdmin(inviteEmail);
    },
    async enrollAdmin(registerUser: FormData) {
      return await UserAPI.enrollAdmin(registerUser);
    },
    async inviteOwner(inviteEmail: InviteOwner[]) {
      return await UserAPI.inviteOwner(inviteEmail);
    },
    async enrollOwner(registerUser: FormData) {
      return await UserAPI.enrollOwner(registerUser);
    },
    async inviteManager(inviteEmail: InviteManager[]) {
      return await UserAPI.inviteManager(inviteEmail);
    },
    async enrollManager(registerUser: FormData) {
      return await UserAPI.enrollManager(registerUser);
    },
    async inviteConsultant(inviteEmail: InviteConsultant[]) {
      return await UserAPI.inviteConsultant(inviteEmail);
    },
    async enrollConsultant(registerUser: FormData) {
      return await UserAPI.enrollConsultant(registerUser);
    },
    async inviteMarketer(inviteEmail: InviteMarketer[]) {
      return await UserAPI.inviteMarketer(inviteEmail);
    },
    async enrollMarketer(registerUser: FormData) {
      return await UserAPI.enrollMarketer(registerUser);
    },
    async validateInviteToken(
      validateInviteToken: ValidateInviteToken
    ): Promise<ValidateInviteTokenResponse> {
      return await UserAPI.validateInviteToken(validateInviteToken);
    },
    async registerUser(registerUser: FormData) {
      this.user = await UserAPI.registerUser(registerUser);
    },
    async checkUsername(username: string) {
      return await UserAPI.checkUsername(username);
    },
    async changePassword(password: ChangePassword) {
      return await UserAPI.changePassword(password);
    },
    async getUserDetailById(id: number) {
      this.UserDetail = await UserAPI.getUserDetailById(id);
    },
    async changeRoleForUser(params: ChangeRole) {
      this.UserDetail = await UserAPI.changeRoleForUser(params);
    },
    async assignFarmsForUser(params: AssignFarm) {
      this.UserDetail = await UserAPI.assignFarmsForUser(params);
    },
    async getAllUsersForAdmin(params?: {
      page?: number;
      search?: string;
      ordering?: string;
      pageSize?: number;
      isActive?: boolean;
    }) {
      // `is_active` is a tri-state filter: true → only active, false → only
      // inactive, undefined → no filter. Stringify so buildQuery's truthy
      // skip doesn't drop the explicit `false`.
      const query = buildQuery({
        page: params?.page,
        search: params?.search,
        ordering: params?.ordering,
        page_size: params?.pageSize,
        is_active: params?.isActive == null ? undefined : String(params.isActive)
      });
      this.Users = await UserAPI.getAllUsersForAdmin(query);
    },
    async getSimpleUsersList(params?: {
      role?: string;
      search?: string;
      ordering?: string;
      isActive?: boolean;
    }) {
      const query = buildQuery({
        custom_user__role: params?.role,
        search: params?.search,
        ordering: params?.ordering,
        is_active: params?.isActive == null ? undefined : String(params.isActive)
      });
      this.SimpleUserList = await UserAPI.getSimpleUserList(query);
    },
    setInvitationTablePagination(data: QTablePagination) {
      this.InvitationTablePagination = data;
    },
    async getInvitations(params: {
      page?: number;
      pageSize?: number;
      search?: string;
      ordering?: string;
    }) {
      const query = buildQuery({
        page: params?.page,
        page_size: params?.pageSize,
        search: params?.search,
        ordering: params?.ordering
      });
      this.PaginatedInvitation = await UserAPI.getInvitations(query);
    },
    async cancelInvitationById(id: number) {
      await UserAPI.cancelInvitationById(id);
    },
    async deleteInvitedUser(data: DeleteInvitedUser) {
      await UserAPI.deleteInvitedUser(data);
      if (this.PaginatedInvitation.results && this.PaginatedInvitation.results.length > 0) {
        const index = this.PaginatedInvitation.results.findIndex(
          x => x.id == data.id
        );
        if (index !== -1) {
          this.PaginatedInvitation.results.splice(index, 1);
        }
      }
    },
    async activateUser(userId: number) {
      this.UserDetail = await UserAPI.activateUser(userId);
    },
    async deactivateUser(userId: number) {
      this.UserDetail = await UserAPI.deactivateUser(userId);
    }
  }
});

